import { trpc } from "@client/trpc";
import { trackNames } from "@lib/types";
import type { HomepageRoundState } from "@lib/homepageRoundState";

export function HomeRoundState({
  roundId,
  viewerId,
}: {
  roundId: string;
  viewerId: string;
}) {
  const query = trpc.getHomepageRoundState.useQuery(
    { roundId, viewerId },
    { staleTime: 0, gcTime: 0, retry: 1, refetchInterval: 30_000 },
  );

  // Never retain a previous viewer's or newly revoked summary after a refetch fails.
  if (query.isError) {
    return (
      <span className="block body-sm text-muted-foreground">
        Round status unavailable
      </span>
    );
  }

  if (
    !query.data ||
    query.data.viewerId !== viewerId ||
    query.data.roundId !== roundId
  ) {
    return (
      <span className="block body-sm text-muted-foreground">
        Loading round status…
      </span>
    );
  }

  return <RoundStateSummary state={query.data} />;
}

export function RoundStateSummary({ state }: { state: HomepageRoundState }) {
  const isAdmin =
    state.roles.includes("Round admin") || state.roles.includes("SFC admin");
  const showMemberTrack = state.usesTracks && state.memberTrack;
  const showRoundProgress = state.roles.includes("Recommender") && !isAdmin;

  const details: string[] = [];

  if (!isAdmin) {
    if (state.roles.length > 0) {
      details.push(state.roles.join(" + "));
    }

    if (showMemberTrack && state.memberTrack) {
      details.push(`${trackNames[state.memberTrack]} track`);
    }
  }

  if (state.completedAt) {
    details.push("Complete");
  } else if (showRoundProgress && !state.archived) {
    details.push("Ongoing");
  }

  if (state.archived) {
    details.push("Archived", "Read-only");
  }

  return details.length > 0 ? (
    <p className="body-sm text-muted-foreground">{details.join(" • ")}</p>
  ) : null;
}
